import { utcToZonedTime, format as dateFormat } from 'date-fns-tz'
import {
  differenceInMonths,
  differenceInWeeks,
  differenceInDays,
  secondsToHours,
  secondsToMinutes,
  parse,
  parseISO,
  isValid
} from 'date-fns'
import Pluralizer from './pluralize'
const pluralizer = new Pluralizer()

export type DateInput = Date | string | number
export type ByUnit = 'day' | 'week' | 'month'
export const userZone = Intl.DateTimeFormat().resolvedOptions().timeZone
export const defaultFormat = 'yyyy-MM-dd HH:mm:ss O'
export const defaultFormatSnapshot = 'yyyy-MM-dd hh:mm a'

export function getToday() {
  return utcToZonedTime(new Date(), userZone)
}

export function getDate(datetime: DateInput) {
  return utcToZonedTime(datetime, userZone)
}

export function createDateFromString(datetime: string, formatString: string) {
  if (isValid(parseISO(datetime))) {
    return parse(datetime, formatString, new Date())
  }
}

export function formatDateTime(
  datetime: DateInput,
  format: string = defaultFormat
): string {
  if (!datetime) {
    return 'N/A'
  }
  return dateFormat(getDate(datetime || 0), format, { timeZone: userZone })
}

export function formatEpochDateTime(epoch: number): string {
  return epoch > 0 ? dateFormat(epoch * 1000, defaultFormat) : 'N/A'
}

export function isSameOrBefore(
  laterDate: Date | number,
  earlierDate: Date | number,
  byUnit: ByUnit
) {
  let result = 0
  switch (byUnit) {
    case 'month':
      result = differenceInMonths(laterDate, earlierDate)
      break

    case 'week':
      result = differenceInWeeks(laterDate, earlierDate)
      break

    case 'day':
      result = differenceInDays(laterDate, earlierDate)
      break

    default:
      break
  }
  return result >= 0
}

export function isRefreshTriggered(lastRefresh, last_run) {
  // Check if lastRefresh (react state) is newer than last_run (backend)
  const defaultDate = '2000-11-11 11:11:11.11 +0000 UTC'
  const lastRun = formatStringToDate(last_run || defaultDate)
  if (lastRefresh instanceof Date) {
    if (lastRun.getTime() < lastRefresh.getTime()) {
      return true
    } else return false
  }
  return false
}
export function formatInterval(seconds = 0): string {
  const hr = secondsToHours(seconds || 0)
  const min = secondsToMinutes(seconds || 0) - hr * 60
  const sec = Math.floor(seconds || 0) - hr * 3600 - min * 60
  if (seconds <= 0) return 'N/A'
  return `${hr > 0 ? `${hr} h` : ''}${min > 0 ? ` ${min} min` : ''}${
    sec > 0 ? ` ${sec} s` : ''
  }`
}

export function formatStringToDate(stringDate: string): Date {
  const replaced = stringDate.replace(/ /g, 'T')
  const trimmed = replaced.replace(/\.[^.]*$/g, 'Z')
  const formattedDate = new Date(trimmed)
  return formattedDate
}

export function calculateTimeDiff(prevTime: Date): string {
  if (prevTime) {
    const timeDiff = new Date().getTime() - prevTime.getTime()
    const minutes = Math.floor(timeDiff / 1000 / 60)
    const hours = Math.floor(minutes / 60)
    if (minutes > 60) {
      return `About ${hours} ${pluralizer.pluralize('hour', hours)} ago`
    } else if (minutes < 1) {
      return `Less than 1 minute ago`
    } else
      return `About ${minutes} ${pluralizer.pluralize('minute', minutes)} ago`
  }
}

export default getDate
